import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError, withNext } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { fmtDate } from "@/components/PriceBlock";

const Shell = ({ children }) => (
  <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16" data-testid="redeem-page">
    <div className="max-w-lg w-full text-center space-y-6 rise">{children}</div>
  </div>
);

const Result = ({ r }) => (
  <Shell>
    <Gift className="w-10 h-10 text-amber-400 mx-auto" />
    <h1 className="font-display text-4xl sm:text-5xl text-amber-50" data-testid="redeem-success-title">Your gift is yours</h1>
    <p className="text-slate-400" data-testid="redeem-success-text">
      {r.redeemed_as === "credit"
        ? `You already pay for Pro, so we've added a $${r.credit} credit to your billing account. It will cover your upcoming bills.`
        : `Pro · Gift · ends ${fmtDate(r.gift_until)}. Unlimited worlds, starting now.`}
    </p>
    <Link to="/worlds" className="inline-block rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-6 py-2.5" data-testid="redeem-go-worlds-btn">Go to my worlds</Link>
  </Shell>
);

export default function RedeemGift() {
  const { code } = useParams();
  const { user, refreshUser } = useAuth();
  const [gift, setGift] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/gifts/redeem/${code}`).then((r) => setGift(r.data)).catch(() => setGift(false)); }, [code]);

  const redeem = async () => {
    setBusy(true);
    try {
      setResult((await api.post(`/gifts/redeem/${code}`)).data);
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (gift === null || user === null) return <Loader />;
  if (result) return <Result r={result} />;
  if (!gift) return <Shell><h1 className="font-display text-4xl text-amber-50" data-testid="redeem-not-found">We couldn't find that gift</h1><p className="text-slate-400">Check the link in your email, or ask the sender to resend it.</p></Shell>;
  const here = `/gift/redeem/${code}`;
  return (
    <Shell>
      <Gift className="w-10 h-10 text-amber-400 mx-auto" />
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80">A gift from {gift.buyer_name}</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-50">A year of unlimited worlds{gift.recipient_name ? `, ${gift.recipient_name}` : ""}</h1>
      {gift.note && <blockquote className="mx-auto max-w-md rounded-xl bg-[#f6f0e1] px-6 py-4 font-display italic text-lg text-[#2b2416] whitespace-pre-wrap break-words" data-testid="redeem-note">{gift.note}</blockquote>}
      {gift.status === "redeemed" ? (
        <p className="text-slate-400" data-testid="redeem-already">This gift has already been redeemed.</p>
      ) : user ? (
        <Button onClick={redeem} disabled={busy} className="rounded-full h-11 px-8 bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="redeem-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Redeem your year of Pro
        </Button>
      ) : (
        <div className="flex flex-wrap justify-center gap-3">
          <Link to={withNext("/register", here)} className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-6 py-2.5" data-testid="redeem-register-btn">Create a free account</Link>
          <Link to={withNext("/login", here)} className="rounded-full border border-line px-6 py-2.5 text-slate-200 hover:border-amber-500/40" data-testid="redeem-login-btn">Sign in</Link>
        </div>
      )}
      {gift.status !== "redeemed" && <p className="text-xs text-slate-500">Your year starts the day you redeem it. When it ends, you keep every world.</p>}
    </Shell>
  );
}
